import { useState, useEffect } from "react";
import "./index.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast, Bounce } from "react-toastify";

// Redux Actions
import {
  schedulesInternalTxnApproval,
  schedulesInternalTxnReject,
} from "../../../redux/features/scheduleInternalTxn/index.js";
import { setHideBreadCrumb } from "../../../redux/features/hideBreadCrumb/hideBreadCrumb";
// Components
import ScheduleInternalTxnDetailPopup from "../../../components/ScheduleInternalTxn/ScheduleInternalTxnDetailPopup/index.jsx";
import Loader from "../../../components/Loader/Index.jsx";
import InternalScheduleTxnDeclinePopup from "../../../components/_utils/TransactionCommonComponents/CommonDeclinePopups/InternalScheduleTxnDeclinePopup.jsx";

// Assets
import ScheduleInternalTransactionMultiDetail from "../../../components/ScheduleInternalTxn/ScheduleInternalTransactionMultiApproval/ScheduleInternalTransactionMultiDetail/index.jsx";
import ApprovalTopNavComponent from "../../../components/_utils/CommonTopNav/ApprovalTopNav/index.jsx";
import UniversalRefreshToast from "../../../components/_utils/UniversalRefreshToast/index.jsx";
import CommonToast from "../../../components/_utils/CommonToast/index.jsx";
import ICONS from "../../../assets/Icons/index.js";

const ScheduleInternalTransactionMultiApproval = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // States
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [isAdditionalPopupOpen, setIsAdditionalPopupOpen] = useState("");
  const [internalScheduleId, setInternalScheduleId] = useState("");
  const [widthCheck, setIswidthCheck] = useState(window.innerWidth <= 950);
  const [selectedCardIds, setSelectedCardIds] = useState([]);
  const [selectedSendTransactions, setSelectedSendTransactions] = useState([]);

  // Selector
  const services = useSelector((state) => state.refreshToken.services);
  const {
    schedulesInternalTxnRejectLoading,
    schedulesInternalTxnApprovalLoading,
  } = useSelector((state) => state.scheduleInternalTxnSlice);

  // Effects
  useEffect(() => {
    dispatch(setHideBreadCrumb({ hide: false }));
    const handleResize = () => {
      setIswidthCheck(window.innerWidth <= 950);
    };
    window.addEventListener("resize", handleResize);

    // Initialize state from session storage
    const sessionData =
      JSON.parse(sessionStorage.getItem("intSelectedScheduleCard")) || [];
    if (sessionData.length > 0) {
      setSelectedSendTransactions(sessionData[0].selectedDetails || []);
      setSelectedCardIds(sessionData[0].selectedIds || []);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [dispatch]);

  useEffect(() => {
    if (
      selectedSendTransactions?.length === 0 &&
      selectedCardIds.length === 0
    ) {
      // Only navigate if we've initialized and found empty, or user unselected all?
      // Ideally check if session *allows* empty, but usually if coming here, we expect data.
      // If state is empty initially, this might trigger before useEffect above runs?
      // No, useEffect runs after render. Initialize state empty usually triggers this?
      // Better to check session directly or use a 'loaded' flag, but adhering to existing logic pattern:
      // Existing logic: if (formatedData?.length === 0) navigate(...)
    }
    const sessionData =
      JSON.parse(sessionStorage.getItem("intSelectedScheduleCard")) || [];
    if (sessionData.length === 0) {
      navigate("/internal_transaction_dashboard/schedule_multi_approval");
    }
  }, [navigate]);

  // Sync state back to session storage
  useEffect(() => {
    if (selectedSendTransactions.length > 0 || selectedCardIds.length > 0) {
      sessionStorage.setItem(
        "intSelectedScheduleCard",
        JSON.stringify([
          {
            selectedDetails: selectedSendTransactions,
            selectedIds: selectedCardIds,
          },
        ]),
      );
    }
  }, [selectedSendTransactions, selectedCardIds]);

  // Handlers
  const closeScheduleInternalTxnModal = () => {
    setIsAdditionalPopupOpen("");
  };

  const handleTxnAction = async (id, reason = "", type) => {
    const isReject = type === "reject";
    const toastMsg = isReject ? "Rejecting schedule" : "Approving schedule";
    const reduxAction = isReject
      ? schedulesInternalTxnReject
      : schedulesInternalTxnApproval;

    setActionLoading(true);
    UniversalRefreshToast(toastMsg);

    const data = await dispatch(
      reduxAction({
        id,
        reason,
        services,
        navigate,
        type: "multiReject",
      }),
    );

    const payload = data?.payload;

    setTimeout(() => {
      setActionLoading(false);
      toast.dismiss();

      if (payload?.status === 500) {
        setTimeout(() =>
          CommonToast({
            text: payload?.error,
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
            buttonNeeded: false,
          }),
        );
      }

      if (payload?.errors?.user_unauthorized?.[0]) {
        setTimeout(() => {
          navigate("/feature_not_assigned", {
            state: { unavailableService: "IAScheduledTxn" },
          });
        }, 1000);
        return;
      }

      if (payload?.status === false) {
        navigate(
          "/internal_transaction_dashboard/schedule_multi_approval/schedules_multi_approval/schedules_multi_transaction_error",
          { state: type },
        );
      } else if (payload?.status === true) {
        const selectedscheduleData = {
          selectedSendTransactions: payload?.transactions,
          selectedTransactions: selectedCardIds,
        };

        sessionStorage.setItem(
          "selectedscheduleData",
          JSON.stringify(selectedscheduleData),
        );
        if (type === "reject") {
          navigate(
            "/internal_transaction_dashboard/schedule_multi_approval/schedules_multi_approval/schedules_multi_transaction_reject",
          );
        } else {
          navigate(
            "/internal_transaction_dashboard/schedule_multi_approval/schedules_multi_approval/schedules_multi_transaction_success",
          );
        }
      }
      if (isReject) setShowRejectPopup(false);
    }, 2000);
  };

  return (
    <>
      {/* Popups */}
      {showRejectPopup && (
        <InternalScheduleTxnDeclinePopup
          allTxnData={selectedSendTransactions}
          handleClose={() => setShowRejectPopup(false)}
          txnId={selectedCardIds}
          updateAllTxn={() => {}}
          flow="multiApproval"
          toastType="multiSchedule"
        />
      )}

      {isAdditionalPopupOpen !== "" && internalScheduleId !== "" ? (
        <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <ScheduleInternalTxnDetailPopup
            closeScheduleInternalTxnModal={closeScheduleInternalTxnModal}
            schedule_internaltxn_id={internalScheduleId}
            detailSelected={isAdditionalPopupOpen}
            typeMulti="MultiApproval"
            type={"bene_account_detail_modal"}
          />
        </div>
      ) : null}

      <div
        className="sch-int-multi-approval-details-main"
        style={{
          overflow: showRejectPopup ? "hidden" : "",
          height: showRejectPopup ? "var(--full-height)" : "",
        }}
      >
        {/* Loading Overlay */}
        {actionLoading ? (
          <div className="fixed top-top-base left-left-base w-screen h-screen flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
            <div className="flex justify-center items-center">
              <Loader />
            </div>
          </div>
        ) : null}

        <div className="hidden Qmd:block min-h-[var(--navbar-breadcrumb-height)] h-navbar-breadcrumb-height bg-primary-nav"></div>

        <div className="sch-int-multi-approval-container">
          <ApprovalTopNavComponent
            topNavCtaBtn1Needed={false}
            topNavCtaBtn1Props={{
              btnText: "Previous",
              btnType: "button",
              enabledBgColor: "var(--primary-nav-act)",
              handleFunction: () => navigate(-1),
              enabledTextColor: "var(--text-primary)",
              isDisabled: false,
              hoverOption: "white",
            }}
            topNavCtaBtn2Needed={false}
            topNavCtaBtnWithIcon1Props={{
              btnText: "Reject schedules",
              handleFunction: () => setShowRejectPopup(true),
              enabledBgColor: "var(--failed)",
              enabledTextColor: "var(--text-primary)",
              icon: "https://q2pay.in/new_assets/common/approval/reject_icon_red_with_white_circle.svg",
              iconToBeAlign: "right",
            }}
            topNavCtaBtnWithIcon2Props={{
              btnText: "Approve schedules",
              handleFunction: () => handleTxnAction(selectedCardIds),
              enabledBgColor: "var(--positive-act)",
              enabledTextColor: "var(--text-primary)",
              icon: "https://q2pay.in/new_assets/common/approval/approve_circle_green_checkmark.svg",

              iconToBeAlign: "right",
            }}
            BottomHeader={`Schedule approval process`}
            BottomSubText={`Below are the schedules details. Approved schedules will generate transactions according to the specified frequency.`}
            isToggleNeeded={false}
            selectionBtnProps={{
              btnText: "View selected",
              btnType: "button",
              enabledBgColor: "var(--primary-nav-act)",
              handleFunction: () => {},
              enabledTextColor: "var(--text-primary)",
              isDisabled: false,
              icon: "https://q2pay.in/new_assets/common/view_all/reload_refresh_white.svg",
            }}
            iconComponentArray={[
              {
                enabledBgColor: "var(--primary-bg)",
                isLoading: actionLoading,
                handleFunction: () => {},
                isDisabled: false,
                icon: "https://q2pay.in/new_assets/common/view_all/reload_refresh_white.svg",
                title: "Refresh schedules",
                hoverOption: "white",
              },
            ]}
            assignLabelNeeded={false}
            assignTagNeeded={false}
          />
        </div>

        {/* Cards Grid */}
        <div className="sch-int-multi-approval-select-data-card">
          <h4>Selected Transactions ({selectedCardIds?.length})</h4>
          <div className="standard-card-alignment gap-gap-md">
            {selectedSendTransactions?.map((data, index) => {
              return (
                <div key={index}>
                  <ScheduleInternalTransactionMultiDetail
                    data={data}
                    selectedCardIds={selectedCardIds}
                    setSelectedCardIds={setSelectedCardIds}
                    selectedSendTransactions={selectedSendTransactions}
                    setSelectedSendTransactions={setSelectedSendTransactions}
                    setIsAdditionalPopupOpen={setIsAdditionalPopupOpen}
                    setInternalScheduleId={setInternalScheduleId}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default ScheduleInternalTransactionMultiApproval;
